import {type} from 'arktype';
import React from 'react';
import {useSelector} from 'react-redux';
import {Navigate, Route, Routes, useParams} from 'react-router';
import {LoadingScreen, NotFoundPage} from '~/entities/fallbacks';
import {AccountId} from '~/entities/operations-fiat';
import {
    ExchangeFiat,
    selectExchangeData,
    selectTopupData,
    TopupPage,
    TopupParams,
    WithdrawPage,
    WithdrawParams,
} from '~/processes/fiat-operations';
import {selectWithdrawData} from '~/processes/fiat-operations/model/selectors/selectWithdrawData';
import {FinancialData, selectFinancesDataStatus, selectFinancesResource} from '~/redux/finances';

export function FiatOperationRouter() {
    const financesStatus = useSelector(selectFinancesDataStatus);
    const doneFetching = financesStatus === 'success' || financesStatus === 'failure';

    if (!doneFetching) {
        return <LoadingScreen />;
    }

    return (
        <Routes>
            <Route
                path={'/topup/fiat/:accountId/:methodType?/:methodId?'}
                element={
                    <Connect params={TopupParams} selector={selectTopupData}>
                        {data => <TopupPage {...data} />}
                    </Connect>
                }
            />

            <Route
                path={'/withdraw/fiat/:accountId/:methodType?/:methodId?'}
                element={
                    <Connect params={WithdrawParams} selector={selectWithdrawData}>
                        {data => <WithdrawPage {...data} />}
                    </Connect>
                }
            />

            <Route
                path='/exchange/fiat/:accountId/fiat/:targetId'
                element={
                    <Connect params={{accountId: AccountId, targetId: AccountId}} selector={selectExchangeData}>
                        {data => <ExchangeFiat {...data} />}
                    </Connect>
                }
            />

            <Route path='*' element={<NotFoundPage />} />
        </Routes>
    );
}

function Connect<TProps, const def>({
    params: paramsDef = {},
    selector,
    children,
}: {
    params?: type.validate<def> | {};
    selector: (params: type.infer<def>) => (finData: FinancialData) => TProps | null;
    children: (data: NonNullable<TProps>, params: type.infer<def>) => React.JSX.Element;
}) {
    const someParams = parseParams(useParams());
    const params = type.raw(paramsDef).assert(someParams);
    const resource = useSelector((state: AppModel) => selectFinancesResource(state).mapSuccess(selector(params)));

    if (resource.data) {
        return children(resource.data, params);
    }
    if (resource.isError) {
        return <Navigate replace to='/assets' />;
    }
    if (resource.isSuccess) {
        return <NotFoundPage />;
    }
    return <LoadingScreen />;
}

function parseParams(params: Record<string, string | undefined>) {
    return Object.fromEntries(
        Object.entries(params)
            .filter(hasValue)
            .map(([key, value]) => [key, maybeNumber(value)])
    );
}

function hasValue(entry: [string, string | undefined]): entry is [string, string] {
    return entry[1] !== undefined;
}

function maybeNumber(param: string) {
    if (param.match(/^\d+$/)) {
        return Number(param);
    }
    return param;
}
